/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {FabMessageService} from '@fabric/components/message';
import {
  ColDef,
  RowSelectionOptions,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {DISMISS_MSG} from '../../../common/constants';
import {AddPopUpComponent} from '../../../core/components/pop-ups/add-pop-up/add-pop-up.component';
import {DeletePopUpComponent} from '../../../core/components/pop-ups/delete-pop-up/delete-pop-up.component';
import {UpdatePopUpComponent} from '../../../core/components/pop-ups/update-pop-up/update-pop-up.component';
import {ScenarioCategoryListService} from '../../../services/scenario-category-list.service';

@Component({
    selector: 'app-manage-scenario-category-list',
    templateUrl: './manage-scenario-category-list.component.html',
    styleUrls: ['./manage-scenario-category-list.component.scss'],
    standalone: false
})
export class ManageScenarioCategoryListComponent implements OnInit {

  showSpinner: boolean;
  initialLoad: boolean;
  noData: boolean;

  columnHeaders: ColDef[] = [
    {field: 'scenarioCategoryId', headerName: 'ID'},
    {field: 'scenarioCategory', headerName: 'Scenario Category'},
  ];
  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitGridWidth",
  };
  rowSelection: RowSelectionOptions = {
    mode: 'singleRow',
    checkboxes: false,
    enableClickSelection: true,
    enableSelectionWithoutKeys: true,
  };

  scenarioCategories: any[] = [];
  selectedValue = '';


  constructor(public snackBar: FabMessageService,
              private readonly scenarioCategoryListService: ScenarioCategoryListService,
              public dialog: MatDialog) {
  }

  ngOnInit(): void {
    this.loadScenarioCategoryList();
  }

  /**
   * fetches data from backend, loads it into scenario categories, and displays it in the table
   * if list is empty, hides table and displays message telling the user the table is empty
   * @return void
   */
  loadScenarioCategoryList(): void {
    this.showSpinner = true;
    this.initialLoad = false;
    this.scenarioCategoryListService.getScenarioCategoryList().subscribe(
      data => {
        this.scenarioCategories = data;
      },
      err => {
        this.errorHandle(err);
      },
      () => {
        this.showSpinner = false;
        this.initialLoad = true;
        this.noData = (this.scenarioCategories.length === 0);
      }
    );
  }

  /**
   * Event for selecting a scenario category
   * @param event the event object
   */
  onSelect(event) {
    if (event.node.selected) {
      this.selectedValue = (event.data.scenarioCategory);
    } else if (event.data.scenarioCategory === this.selectedValue) {
      this.selectedValue = '';
    }
  }

  /**
   * Add a new Scenario Category in DB.
   */
  addScenarioCategory(): void {
    const dialogRef = this.dialog.open(AddPopUpComponent, {
      data: {
        title: 'Add',
        maxLength: 50
      }
    });
    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.scenarioCategoryListService.addScenarioCategory(res.data.newValue).subscribe({
          error: err => {
            this.errorHandle(err);
            this.loadScenarioCategoryList();
          },
          complete: () => {
            this.snackBar.displaySuccess('Successfully Added.', DISMISS_MSG);
            this.loadScenarioCategoryList();
          }
        });
      }
    });
  }

  /**
   * Delete a selected Scenario Category from DB.
   */
  deleteScenarioCategory(): void {
    const dialogRef = this.dialog.open(DeletePopUpComponent, {
      data: {
        title: 'Delete',
        valueToDelete: this.selectedValue
      }
    });

    dialogRef.afterClosed().subscribe(res => {
      // received data from dialog-component
      if (res.data.deleteValue) {
        this.scenarioCategoryListService.deleteScenarioCategory(this.selectedValue).subscribe({
          error: err => {
            this.errorHandle(err);
            this.loadScenarioCategoryList();
          },
          complete: () => {
            this.snackBar.displaySuccess('Successfully removed scenario category', DISMISS_MSG);
            this.loadScenarioCategoryList();
            this.selectedValue = '';
          }
        });
      }
    });
  }

  /**
   * Update selected Scenario Category with new value.
   */
  editScenarioCategory(): void {
    const dialogRef = this.dialog.open(UpdatePopUpComponent, {
      data: {
        title: 'Update',
        currentValueToUpdate: this.selectedValue,
        hidePreview: true,
        maxLength: 50
      }
    });

    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.scenarioCategoryListService.editScenarioCategory(this.selectedValue, res.data.newValue).subscribe(
          () => {
            this.snackBar.displaySuccess('Successfully updated scenario category', DISMISS_MSG);
            this.loadScenarioCategoryList();
          },
          (error) => {
            this.errorHandle(error);
          },
          () => {
            this.selectedValue = '';
          }
        );
      }
    });
  }

  errorHandle(error: string): void {
    this.showSpinner = false;
    this.selectedValue = '';
    this.snackBar.displayError(error, DISMISS_MSG);
  }
}

